'use client';

import { UnifiedDebugPanel } from '@/lib/mcp/components/UnifiedDebugPanel';
import Image from 'next/image';

interface DebugImageOverlayProps {
  imageName: string;
  imageUrl: string;
}

export function DebugImageOverlay({ imageName, imageUrl }: DebugImageOverlayProps) {
  // Extract image name from URL if provided as full path
  const extractedImageName = imageName || imageUrl.split('/').pop()?.split('.')[0] || '';
  
  // Determine which service is being used
  const imageService = process.env.NEXT_PUBLIC_IMAGE_SERVICE || 'api';
  const serviceUrl = imageService === 'api' 
    ? (process.env.NEXT_PUBLIC_API_SERVER || 'http://localhost:8000')
    : (process.env.NEXT_PUBLIC_MCP_SERVER || 'http://localhost:5000');

  // Handle refresh request (e.g., after image generation)
  const handleRefresh = () => {
    // Force a refresh of the image by adding a timestamp query parameter
    const img = document.querySelector(`img[src^="${imageUrl.split('?')[0]}"]`) as HTMLImageElement;
    if (img) {
      img.src = `${imageUrl.split('?')[0]}?t=${Date.now()}`;
    }
  };

  return (
    <div className="fixed inset-0 bg-transparent flex items-center justify-end z-50">
      <div className="h-full w-full md:w-5/6 lg:w-3/4 xl:w-2/3 bg-white border-l shadow-lg p-4 md:p-6 overflow-auto">
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">{extractedImageName}</h2>
            <div className="flex items-center gap-2 text-xs">
              <span className={`inline-block w-2 h-2 rounded-full ${
                imageService === 'api' ? 'bg-success' : 'bg-info'
              }`} title={`Using ${imageService.toUpperCase()} service`}></span>
              <span className={`text-foreground px-2 py-1 rounded-full ${
                imageService === 'api' ? 'bg-success/10' : 'bg-info/10'
              }`}>
                {imageService.toUpperCase()} ({serviceUrl})
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => window.dispatchEvent(new Event('closeDebugOverlay'))}
                className="rounded-full p-1 hover:bg-accent"
                title="Close"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>

          {/* Use grid for 1/3 : 2/3 layout on larger screens */}
          <div className="flex flex-col lg:grid lg:grid-cols-3 gap-6 lg:gap-8 flex-grow min-h-0">
            {/* Image Preview - takes 1/3 */}
            <div className="lg:col-span-1 flex flex-col items-center lg:items-start">
              {/* Make image container fill available space but constrain height */}
              <div className="relative w-full max-w-md lg:max-w-full h-48 lg:h-64 mb-4">
                <Image 
                  src={imageUrl} 
                  alt={extractedImageName} 
                  fill
                  className="object-contain"
                  sizes="(max-width: 1024px) 90vw, 30vw"
                />
              </div>
            </div>
            
            {/* UnifiedDebugPanel - takes 2/3 */}
            <div className="lg:col-span-2 min-w-0 flex flex-col flex-grow">
              <UnifiedDebugPanel
                imageName={extractedImageName}
                serviceUrl={serviceUrl}
                onRefresh={handleRefresh}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}